// src/pages/inventory/InventoryDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  LinearProgress,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Fab,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  Tab,
  Tabs,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  History as HistoryIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  Store as StoreIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  Notifications as NotificationIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Category as CategoryIcon,
  RestaurantMenu as MenuIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  AddShoppingCart as StockIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

// Import Recharts for charts
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from 'recharts';

// Import theme colors and preloader
import { COLORS } from '../../../theme/colors';
import Preloader from '../../../theme/Preloader';

// Lazy load components
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/lounge/Sidebar'));
const Header = lazy(() => import('../../../components/RestaurantPOS/manager/Header'));
const Footer = lazy(() => import('../../../components/RestaurantPOS/manager/Footer'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getAuthToken = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token || isTokenExpired(token)) {
      return null;
    }
    return token;
  } catch (err) {
    return null;
  }
};

// ============================================
// UTILITY FUNCTIONS & CONSTANTS
// ============================================

const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: COLORS.primary?.african || '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    sidebar: AfricanSidebar,
    path: '/african-inventory-dashboard'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: COLORS.primary?.continental || '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    sidebar: ContinentalSidebar,
    path: '/continental-inventory-dashboard'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: COLORS.primary?.lounge || '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    sidebar: LoungeSidebar,
    path: '/lounge-inventory-dashboard'
  }
};

const STATUS_COLORS = {
  IN_STOCK: '#4caf50',
  LOW_STOCK: '#ff9800',
  OUT_OF_STOCK: '#f44336',
  DISCONTINUED: '#9e9e9e',
};

const STATUS_LABELS = {
  IN_STOCK: 'In Stock',
  LOW_STOCK: 'Low Stock',
  OUT_OF_STOCK: 'Out of Stock',
  DISCONTINUED: 'Discontinued',
};

const MOVEMENT_TYPE_LABELS = {
  PURCHASE: 'Purchase',
  SALE: 'Sale',
  ADJUSTMENT: 'Adjustment',
  RETURN: 'Return',
  WASTAGE: 'Wastage',
  TRANSFER: 'Transfer',
};

const MOVEMENT_TYPE_COLORS = {
  PURCHASE: '#4caf50',
  SALE: '#f44336',
  ADJUSTMENT: '#ff9800',
  RETURN: '#2196f3',
  WASTAGE: '#9e9e9e',
  TRANSFER: '#9c27b0',
};

const MAIN_CATEGORIES = ['Food', 'Drinks'];
const CHART_COLORS = ['#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0', '#00bcd4'];

// ============================================
// SUB-COMPONENTS
// ============================================

// Stat Card Component
const StatCard = React.memo(({ title, value, icon, color, subtitle, trend }) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 32, height: 32 }}>
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color, mt: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Chip
            size="small"
            label={trend}
            icon={trend.startsWith('+') ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
            sx={{
              mt: 0.5,
              backgroundColor: alpha(trend.startsWith('+') ? '#4caf50' : '#f44336', 0.1),
              color: trend.startsWith('+') ? '#4caf50' : '#f44336',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        )}
      </CardContent>
    </Card>
  );
});

// Menu Item Row with Inventory Status
const MenuItemWithInventoryRow = React.memo(({ 
  item, 
  inventoryItem,
  onEdit, 
  onDelete, 
  onToggle,
  onAddStock,
  onViewStock,
  onViewDetails,
  index 
}) => {
  const theme = useTheme();
  const statusColor = item.is_active ? '#4caf50' : '#f44336';
  const statusLabel = item.is_active ? 'Active' : 'Inactive';
  const mainCategoryColor = item.main_category === 'Food' ? '#FF6F00' : '#00695C';
  
  const hasInventory = !!inventoryItem;
  const stockStatus = inventoryItem?.status || 'NO_INVENTORY';
  const stockQuantity = inventoryItem?.quantity || 0;
  const stockUnit = inventoryItem?.unit || 'unit';
  
  const stockStatusColor = stockStatus === 'IN_STOCK' ? '#4caf50' : 
                           stockStatus === 'LOW_STOCK' ? '#ff9800' : 
                           stockStatus === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e';
  const stockStatusLabel = stockStatus === 'NO_INVENTORY' ? 'No Inventory' : 
                           STATUS_LABELS[stockStatus] || stockStatus;

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            {item.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {item.category}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.main_category}
          size="small"
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(mainCategoryColor, 0.1),
            color: mainCategoryColor,
            fontSize: '0.75rem',
          }}
          icon={item.main_category === 'Food' ? <FoodIcon sx={{ fontSize: 14 }} /> : <DrinkIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700} color="primary">
          ₦{item.amount?.toFixed(2) || '0.00'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{
            fontSize: '0.85rem',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.description || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${stockQuantity} ${stockUnit}`}
            size="small"
            sx={{
              backgroundColor: alpha(stockStatusColor, 0.1),
              color: stockStatusColor,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
          <Chip
            label={stockStatusLabel}
            size="small"
            sx={{
              backgroundColor: alpha(stockStatusColor, 0.15),
              color: stockStatusColor,
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onViewDetails(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(item)}
              sx={{ color: theme.palette.primary.main }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Stock">
            <IconButton
              size="small"
              onClick={() => onAddStock(item, inventoryItem)}
              sx={{ color: '#4caf50' }}
            >
              <StockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Stock">
            <IconButton
              size="small"
              onClick={() => onViewStock(item, inventoryItem)}
              sx={{ color: theme.palette.info.main }}
            >
              <StoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={item.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => onToggle(item)}
              sx={{ 
                color: item.is_active ? theme.palette.warning.main : theme.palette.success.main 
              }}
            >
              {item.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(item)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// Inventory Item Row Component for Table
const InventoryItemRow = React.memo(({ 
  item, 
  onEdit, 
  onDelete, 
  onAddStock, 
  onViewMovements,
  onViewDetails,
  index 
}) => {
  const theme = useTheme();
  const statusColor = STATUS_COLORS[item.status] || '#9e9e9e';
  const statusLabel = STATUS_LABELS[item.status] || item.status;

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            {item.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {item.category || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {item.quantity} {item.unit}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          ₦{item.cost_per_unit?.toFixed(2) || '0.00'}
        </Typography>
      </TableCell>
      <TableCell>
        {item.menu_item_name ? (
          <Chip
            label={item.menu_item_name}
            size="small"
            icon={<LinkIcon sx={{ fontSize: 14 }} />}
            sx={{ fontWeight: 500 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">Not linked</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onViewDetails(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Stock">
            <IconButton
              size="small"
              onClick={() => onAddStock(item)}
              sx={{ color: '#4caf50' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Movements">
            <IconButton
              size="small"
              onClick={() => onViewMovements(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(item)}
              sx={{ color: theme.palette.primary.main }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(item)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// Movement History Row Component
const MovementRow = React.memo(({ movement }) => {
  const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
  const typeColor = MOVEMENT_TYPE_COLORS[movement.movement_type] || '#9e9e9e';
  const typeLabel = MOVEMENT_TYPE_LABELS[movement.movement_type] || movement.movement_type;

  return (
    <TableRow>
      <TableCell>{new Date(movement.created_at).toLocaleString()}</TableCell>
      <TableCell>
        <Chip
          label={typeLabel}
          size="small"
          sx={{
            backgroundColor: alpha(typeColor, 0.1),
            color: typeColor,
            fontWeight: 600,
          }}
          icon={isIn ? <InIcon sx={{ fontSize: 14 }} /> : <OutIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
        {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
      </TableCell>
      <TableCell>{movement.previous_quantity} → {movement.new_quantity}</TableCell>
      <TableCell>{movement.reason || '-'}</TableCell>
      <TableCell>{movement.performed_by_name || '-'}</TableCell>
    </TableRow>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const InventoryDashboard = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================

  // Menu items state
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  
  // Inventory items state
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  
  // Inventory items mapped by menu_item_id for quick lookup
  const [inventoryMap, setInventoryMap] = useState({});
  
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    total_items: 0,
    active_items: 0,
    inactive_items: 0,
    low_stock_count: 0,
    out_of_stock_count: 0,
    total_quantity: 0,
    total_value: 0,
    categories: {},
    menu_item_count: 0,
    menu_items_with_inventory: 0,
  });
  
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Inventory Dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Filter state for menu items
  const [mainCategoryFilter, setMainCategoryFilter] = useState('all');
  const [menuStatusFilter, setMenuStatusFilter] = useState('all');
  const [stockStatusFilter, setStockStatusFilter] = useState('all');

  // Filter state for inventory items
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // Stock dialog state with stepper
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockItem, setStockItem] = useState(null);
  const [stockMenuItem, setStockMenuItem] = useState(null);
  const [stockQuantity, setStockQuantity] = useState('');
  const [stockReason, setStockReason] = useState('');
  const [stockCost, setStockCost] = useState('');
  const [stockNotes, setStockNotes] = useState('');
  const [stocking, setStocking] = useState(false);
  const [stockStep, setStockStep] = useState(0);

  // Toggle dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [itemToToggle, setItemToToggle] = useState(null);
  const [toggling, setToggling] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Movement history dialog
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [movementHistory, setMovementHistory] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // View Stock dialog state
  const [viewStockDialogOpen, setViewStockDialogOpen] = useState(false);
  const [viewStockItem, setViewStockItem] = useState(null);
  const [viewStockData, setViewStockData] = useState(null);
  const [viewStockMovements, setViewStockMovements] = useState([]);
  const [loadingStockData, setLoadingStockData] = useState(false);

  // Form state
  const defaultFormData = useMemo(() => ({
    name: '',
    description: '',
    category: '',
    unit: 'unit',
    quantity: 0,
    minimum_stock_level: 5,
    reorder_point: 10,
    reorder_quantity: null,
    cost_per_unit: null,
    selling_price: null,
    supplier: '',
    supplier_contact: '',
    batch_number: '',
    expiry_date: '',
    menu_item_id: null,
    is_active: true,
  }), []);

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Chart data
  const [trendData, setTrendData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  // Get location config
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;
  const SidebarComponent = locationConfig.sidebar;

  // ============================================
  // API FUNCTIONS
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const url = `${FULL_API_URL}${endpoint}`;
    
    try {
      const response = await fetch(url, { ...options, headers });
      
      if (response.status === 401) {
        localStorage.clear();
        window.location.href = '/restaurantAuth';
        throw new Error('Session expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      
      return data;
    } catch (err) {
      console.error('[InventoryDashboard] API Error:', err);
      throw err;
    }
  }, []);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchMenuItems = useCallback(async () => {
    try {
      const data = await apiRequest(`/menu-items/?location_id=${locationIdValue}&limit=1000`);
      const itemsData = Array.isArray(data) ? data : [];
      setMenuItems(itemsData);
      setFilteredMenuItems(itemsData);
      return itemsData;
    } catch (err) {
      console.error('[InventoryDashboard] Error fetching menu items:', err);
      return [];
    }
  }, [apiRequest, locationIdValue]);

  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/inventory/location/${locationName}/items?limit=1000`);
      const itemsData = Array.isArray(data) ? data : [];
      setInventoryItems(itemsData);
      setFilteredInventoryItems(itemsData);
      
      // Create inventory map for quick lookup
      const map = {};
      itemsData.forEach(item => {
        if (item.menu_item_id) {
          map[item.menu_item_id] = item;
        }
      });
      setInventoryMap(map);
      
      // Calculate stats from items
      const total = itemsData.length;
      const active = itemsData.filter(i => i.is_active !== false).length;
      const lowStock = itemsData.filter(i => i.status === 'LOW_STOCK').length;
      const outOfStock = itemsData.filter(i => i.status === 'OUT_OF_STOCK').length;
      const totalQuantity = itemsData.reduce((sum, i) => sum + (i.quantity || 0), 0);
      const totalValue = itemsData.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cost_per_unit || 0)), 0);
      
      // Category breakdown
      const categories = {};
      itemsData.forEach(item => {
        const cat = item.category || 'Uncategorized';
        categories[cat] = (categories[cat] || 0) + 1;
      });
      
      setStats(prev => ({
        ...prev,
        total_items: total,
        active_items: active,
        inactive_items: total - active,
        low_stock_count: lowStock,
        out_of_stock_count: outOfStock,
        total_quantity: totalQuantity,
        total_value: totalValue,
        categories,
      }));

      // Prepare category chart data
      const categoryChartData = Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      }));
      setCategoryData(categoryChartData);

      return itemsData;
    } catch (err) {
      console.error('[InventoryDashboard] Error fetching inventory:', err);
      setSnackbar({
        open: true,
        message: `❌ Failed to fetch inventory: ${err.message}`,
        severity: 'error',
      });
      return [];
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationName]);

  const fetchMovements = useCallback(async () => {
    try {
      const data = await apiRequest(`/inventory/movements?location_id=${locationIdValue}&limit=200`);
      const movementsData = Array.isArray(data) ? data : [];
      setMovements(movementsData);
      
      // Prepare trend data from movements
      const trendMap = {};
      movementsData.forEach(m => {
        const date = new Date(m.created_at).toLocaleDateString();
        if (!trendMap[date]) {
          trendMap[date] = { date, purchases: 0, sales: 0, adjustments: 0 };
        }
        if (m.movement_type === 'PURCHASE') {
          trendMap[date].purchases += m.quantity;
        } else if (m.movement_type === 'SALE') {
          trendMap[date].sales += m.quantity;
        } else {
          trendMap[date].adjustments += Math.abs(m.quantity);
        }
      });
      
      const trendArray = Object.values(trendMap).slice(-14);
      setTrendData(trendArray);
      
    } catch (err) {
      console.error('[InventoryDashboard] Error fetching movements:', err);
    }
  }, [apiRequest, locationIdValue]);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await apiRequest(`/inventory/alerts?location_id=${locationIdValue}&resolved=false`);
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[InventoryDashboard] Error fetching alerts:', err);
    }
  }, [apiRequest, locationIdValue]);

 // ============================================
// FIXED: FETCH MOVEMENTS FOR ITEM - RETURNS DATA
// ============================================

const fetchMovementsForItem = useCallback(async (itemId) => {
  setLoadingMovements(true);
  try {
    const data = await apiRequest(`/inventory/movements?inventory_item_id=${itemId}&limit=200`);
    const movementsData = Array.isArray(data) ? data : [];
    setMovementHistory(movementsData);
    return movementsData; // Return the data directly
  } catch (err) {
    console.error('[InventoryDashboard] Error fetching movements:', err);
    setSnackbar({
      open: true,
      message: `Failed to fetch movements: ${err.message}`,
      severity: 'error',
    });
    return [];
  } finally {
    setLoadingMovements(false);
  }
}, [apiRequest]);

  const fetchInventoryForMenuItem = useCallback(async (menuItemId) => {
    try {
      const endpoint = `/inventory/items?menu_item_id=${menuItemId}&location_id=${locationIdValue}`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data[0] : null;
    } catch (err) {
      console.error('[InventoryDashboard] Error fetching inventory:', err);
      return null;
    }
  }, [apiRequest, locationIdValue]);

  const fetchAllData = useCallback(async () => {
    const [menuData, inventoryData] = await Promise.all([
      fetchMenuItems(),
      fetchInventoryItems(),
    ]);
    
    // Update stats with menu item counts
    const menuItemsWithInventory = inventoryData.filter(i => i.menu_item_id).length;
    setStats(prev => ({
      ...prev,
      menu_item_count: menuData.length,
      menu_items_with_inventory: menuItemsWithInventory,
    }));
    
    await Promise.all([
      fetchMovements(),
      fetchAlerts(),
    ]);
  }, [fetchMenuItems, fetchInventoryItems, fetchMovements, fetchAlerts]);

  // ============================================
  // CRUD OPERATIONS - Menu Items
  // ============================================

  const handleToggleMenuItemStatus = useCallback(async (id, currentStatus) => {
    setToggling(true);
    try {
      const newStatus = !currentStatus;
      const result = await apiRequest(`/menu-items/${id}/toggle-status`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: newStatus }),
      });
      
      setSnackbar({
        open: true,
        message: `✅ Menu item "${result.name}" ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        severity: 'success',
      });
      
      await fetchAllData();
      handleCloseToggleDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to toggle status: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setToggling(false);
    }
  }, [apiRequest, fetchAllData]);

  const handleDeleteMenuItem = useCallback(async (id) => {
    setDeleting(true);
    try {
      const result = await apiRequest(`/menu-items/${id}/permanent`, {
        method: 'DELETE',
      });
      
      setSnackbar({
        open: true,
        message: `✅ ${result.message || 'Menu item deleted successfully!'}`,
        severity: 'success',
      });
      
      await fetchAllData();
      handleCloseDeleteDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to delete menu item: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  }, [apiRequest, fetchAllData]);

  // ============================================
  // CRUD OPERATIONS - Inventory
  // ============================================

  const handleCreateInventoryItem = useCallback(async (data) => {
    const submitData = {
      ...data,
      location_id: locationIdValue,
      quantity: parseFloat(data.quantity) || 0,
      minimum_stock_level: parseFloat(data.minimum_stock_level) || 5,
      reorder_point: parseFloat(data.reorder_point) || 10,
      cost_per_unit: data.cost_per_unit ? parseFloat(data.cost_per_unit) : null,
      selling_price: data.selling_price ? parseFloat(data.selling_price) : null,
      menu_item_id: data.menu_item_id ? parseInt(data.menu_item_id) : null,
    };

    const result = await apiRequest('/inventory/items', {
      method: 'POST',
      body: JSON.stringify(submitData),
    });

    setSnackbar({
      open: true,
      message: `✅ Inventory item "${result.name}" created successfully!`,
      severity: 'success',
    });

    await fetchAllData();
    return result;
  }, [apiRequest, locationIdValue, fetchAllData]);

  const handleUpdateInventoryItem = useCallback(async (id, data) => {
    const submitData = {
      ...data,
      minimum_stock_level: data.minimum_stock_level ? parseFloat(data.minimum_stock_level) : undefined,
      reorder_point: data.reorder_point ? parseFloat(data.reorder_point) : undefined,
      cost_per_unit: data.cost_per_unit ? parseFloat(data.cost_per_unit) : undefined,
      selling_price: data.selling_price ? parseFloat(data.selling_price) : undefined,
      menu_item_id: data.menu_item_id ? parseInt(data.menu_item_id) : null,
    };

    const result = await apiRequest(`/inventory/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(submitData),
    });

    setSnackbar({
      open: true,
      message: `✅ Inventory item "${result.name}" updated successfully!`,
      severity: 'success',
    });

    await fetchAllData();
    return result;
  }, [apiRequest, fetchAllData]);

  const handleAddStock = useCallback(async () => {
    if (!stockItem) {
      setSnackbar({
        open: true,
        message: 'No item selected for stock addition',
        severity: 'error',
      });
      return;
    }
    
    const quantity = parseFloat(stockQuantity);
    if (!quantity || quantity <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid quantity greater than 0',
        severity: 'error',
      });
      return;
    }

    setStocking(true);
    try {
      // If we have a menu item and no inventory item yet, create one
      if (stockMenuItem && !stockItem.id) {
        const newInventory = await handleCreateInventoryItem({
          name: stockMenuItem.name,
          description: stockMenuItem.description || `Stock for ${stockMenuItem.name}`,
          category: stockMenuItem.category || 'Uncategorized',
          unit: 'unit',
          quantity: 0,
          minimum_stock_level: 5,
          reorder_point: 10,
          cost_per_unit: stockCost ? parseFloat(stockCost) : null,
          menu_item_id: stockMenuItem.id,
          supplier: '',
        });
        
        setSnackbar({
          open: true,
          message: `📦 Inventory item created for "${stockMenuItem.name}"`,
          severity: 'info',
        });
        
        // Now add stock to the newly created inventory item
        const currentQuantity = newInventory.quantity || 0;
        const newQuantity = currentQuantity + quantity;

        await apiRequest(`/inventory/items/${newInventory.id}/stock`, {
          method: 'PATCH',
          body: JSON.stringify({
            quantity: newQuantity,
            reason: stockReason || 'Stock addition from menu item',
            notes: stockNotes || '',
          }),
        });
        
        // Create movement record with cost if provided
        if (stockCost) {
          await apiRequest('/inventory/movements', {
            method: 'POST',
            body: JSON.stringify({
              inventory_item_id: newInventory.id,
              movement_type: 'PURCHASE',
              quantity: quantity,
              reason: stockReason || 'Stock purchase',
              cost_per_unit: parseFloat(stockCost),
              notes: stockNotes || '',
            }),
          });
        }
        
        setSnackbar({
          open: true,
          message: `✅ Added ${quantity} units to "${stockMenuItem.name}" stock successfully!`,
          severity: 'success',
        });
        
      } else {
        // Existing inventory item
        const currentQuantity = stockItem.quantity || 0;
        const newQuantity = currentQuantity + quantity;

        await apiRequest(`/inventory/items/${stockItem.id}/stock`, {
          method: 'PATCH',
          body: JSON.stringify({
            quantity: newQuantity,
            reason: stockReason || 'Stock addition',
            notes: stockNotes || '',
          }),
        });

        // Create movement record with cost if provided
        if (stockCost) {
          await apiRequest('/inventory/movements', {
            method: 'POST',
            body: JSON.stringify({
              inventory_item_id: stockItem.id,
              movement_type: 'PURCHASE',
              quantity: quantity,
              reason: stockReason || 'Stock purchase',
              cost_per_unit: parseFloat(stockCost),
              notes: stockNotes || '',
            }),
          });
        }
        
        setSnackbar({
          open: true,
          message: `✅ Added ${quantity} ${stockItem.unit} to "${stockItem.name}" successfully!`,
          severity: 'success',
        });
      }
      
      handleCloseStockDialog();
      await fetchAllData();
    } catch (err) {
      console.error('[InventoryDashboard] Error adding stock:', err);
      setSnackbar({
        open: true,
        message: `Failed to add stock: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setStocking(false);
    }
  }, [
    apiRequest, 
    stockItem, 
    stockMenuItem, 
    stockQuantity, 
    stockReason, 
    stockCost, 
    stockNotes, 
    fetchAllData, 
    handleCreateInventoryItem
  ]);

  const handleDeleteInventoryItem = useCallback(async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    try {
      await apiRequest(`/inventory/items/${itemToDelete.id}`, {
        method: 'DELETE',
      });

      setSnackbar({
        open: true,
        message: `✅ Inventory item "${itemToDelete.name}" deleted successfully!`,
        severity: 'success',
      });

      handleCloseDeleteDialog();
      await fetchAllData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to delete item: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  }, [apiRequest, itemToDelete, fetchAllData]);

  // ============================================
  // DIALOG HANDLERS
  // ============================================

  const handleOpenDialog = useCallback((mode, item = null) => {
    setDialogMode(mode);
    setSelectedItem(item);
    setFormErrors({});
    setSubmitting(false);

    if (mode === 'create') {
      setFormData({
        ...defaultFormData,
        location_id: locationIdValue,
      });
    } else if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category: item.category || '',
        unit: item.unit || 'unit',
        quantity: item.quantity || 0,
        minimum_stock_level: item.minimum_stock_level || 5,
        reorder_point: item.reorder_point || 10,
        reorder_quantity: item.reorder_quantity || '',
        cost_per_unit: item.cost_per_unit || '',
        selling_price: item.selling_price || '',
        supplier: item.supplier || '',
        supplier_contact: item.supplier_contact || '',
        batch_number: item.batch_number || '',
        expiry_date: item.expiry_date || '',
        menu_item_id: item.menu_item_id || null,
        is_active: item.is_active !== undefined ? item.is_active : true,
      });
    }
    setDialogOpen(true);
  }, [locationIdValue, defaultFormData]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedItem(null);
    setFormErrors({});
  }, []);

  const handleOpenStockDialog = useCallback((menuItem, inventoryItem = null) => {
    setStockMenuItem(menuItem);
    setStockItem(inventoryItem || {});
    setStockQuantity('');
    setStockReason('');
    setStockCost('');
    setStockNotes('');
    setStockStep(0);
    setStockDialogOpen(true);
  }, []);

  const handleCloseStockDialog = useCallback(() => {
    setStockDialogOpen(false);
    setStockMenuItem(null);
    setStockItem(null);
    setStockQuantity('');
    setStockReason('');
    setStockCost('');
    setStockNotes('');
    setStocking(false);
    setStockStep(0);
  }, []);

  const handleOpenToggleDialog = useCallback((item) => {
    setItemToToggle(item);
    setToggleDialogOpen(true);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleDialogOpen(false);
    setItemToToggle(null);
    setToggling(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((item) => {
    setItemToDelete(item);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDeleteConfirmation('');
    setDeleting(false);
  }, []);

  const handleOpenMovementsDialog = useCallback(async (item) => {
    setMovementItem(item);
    setMovementDialogOpen(true);
    await fetchMovementsForItem(item.id);
  }, [fetchMovementsForItem]);

  const handleCloseMovementsDialog = useCallback(() => {
    setMovementDialogOpen(false);
    setMovementItem(null);
    setMovementHistory([]);
  }, []);

  const handleViewItem = useCallback((item) => {
    setViewItem(item);
    setViewDialogOpen(true);
  }, []);

  const handleCloseViewDialog = useCallback(() => {
    setViewDialogOpen(false);
    setViewItem(null);
  }, []);

 // ============================================
// FIXED: VIEW STOCK HANDLER
// ============================================
// ============================================
// FIXED: VIEW STOCK HANDLER - COMPLETE
// ============================================

const handleViewStock = useCallback(async (menuItem, inventoryItem = null) => {
  // Reset all states
  setViewStockItem(menuItem);
  setViewStockDialogOpen(true);
  setLoadingStockData(true);
  setViewStockData(null);
  setViewStockMovements([]);
  
  try {
    let inventory = inventoryItem;
    if (!inventory) {
      inventory = await fetchInventoryForMenuItem(menuItem.id);
    }
    
    // Set inventory data
    setViewStockData(inventory || null);
    
    // If inventory exists, fetch movements and store them
    if (inventory) {
      const movements = await fetchMovementsForItem(inventory.id);
      setViewStockMovements(movements || []);
    } else {
      setViewStockMovements([]);
    }
  } catch (err) {
    console.error('[InventoryDashboard] Error fetching stock data:', err);
    setSnackbar({
      open: true,
      message: `Failed to fetch stock data: ${err.message}`,
      severity: 'error',
    });
    setViewStockMovements([]);
  } finally {
    setLoadingStockData(false);
  }
}, [fetchInventoryForMenuItem, fetchMovementsForItem]);


  const handleCloseViewStockDialog = useCallback(() => {
    setViewStockDialogOpen(false);
    setViewStockItem(null);
    setViewStockData(null);
    setViewStockMovements([]);
    setLoadingStockData(false);
  }, []);

  // ============================================
  // FORM HANDLERS
  // ============================================

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const name = formData.name?.trim() || '';
    
    if (!name) {
      errors.name = 'Item name is required';
    } else if (name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.unit) {
      errors.unit = 'Unit is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      if (dialogMode === 'create') {
        await handleCreateInventoryItem(formData);
      } else if (dialogMode === 'edit' && selectedItem) {
        await handleUpdateInventoryItem(selectedItem.id, formData);
      }
      handleCloseDialog();
    } catch (err) {
      console.error('[InventoryDashboard] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [formData, dialogMode, selectedItem, validateForm, handleCreateInventoryItem, handleUpdateInventoryItem]);

  // ============================================
  // SEARCH AND FILTER
  // ============================================

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters to menu items
  const applyMenuFilters = useCallback(() => {
    let filtered = [...menuItems];
    
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }
    
    if (mainCategoryFilter !== 'all') {
      filtered = filtered.filter(item => item.main_category === mainCategoryFilter);
    }
    
    if (menuStatusFilter !== 'all') {
      const isActive = menuStatusFilter === 'active';
      filtered = filtered.filter(item => item.is_active === isActive);
    }
    
    if (stockStatusFilter !== 'all') {
      filtered = filtered.filter(item => {
        const inv = inventoryMap[item.id];
        if (stockStatusFilter === 'has_inventory') {
          return !!inv;
        } else if (stockStatusFilter === 'no_inventory') {
          return !inv;
        } else if (stockStatusFilter === 'in_stock') {
          return inv && inv.status === 'IN_STOCK';
        } else if (stockStatusFilter === 'low_stock') {
          return inv && inv.status === 'LOW_STOCK';
        } else if (stockStatusFilter === 'out_of_stock') {
          return inv && inv.status === 'OUT_OF_STOCK';
        }
        return true;
      });
    }
    
    setFilteredMenuItems(filtered);
    setPage(0);
  }, [menuItems, debouncedSearchTerm, mainCategoryFilter, menuStatusFilter, stockStatusFilter, inventoryMap]);

  // Apply filters to inventory items
  const applyInventoryFilters = useCallback(() => {
    let filtered = [...inventoryItems];
    
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.supplier?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    setFilteredInventoryItems(filtered);
    setPage(0);
  }, [inventoryItems, debouncedSearchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    if (tabValue === 0) {
      applyMenuFilters();
    } else if (tabValue === 1) {
      applyInventoryFilters();
    }
  }, [tabValue, applyMenuFilters, applyInventoryFilters]);

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setMainCategoryFilter('all');
    setMenuStatusFilter('all');
    setStockStatusFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setPage(0);
  }, []);

  // ============================================
  // SORTING
  // ============================================

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
    const list = tabValue === 0 ? filteredMenuItems : filteredInventoryItems;
    const sorted = [...list].sort((a, b) => {
      if (property === 'name') {
        return isAsc ? a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name);
      }
      if (property === 'amount' || property === 'quantity' || property === 'cost_per_unit') {
        const valA = a[property] || 0;
        const valB = b[property] || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      if (property === 'category') {
        return isAsc ? a.category?.localeCompare(b.category) : b.category?.localeCompare(a.category);
      }
      return 0;
    });
    
    if (tabValue === 0) {
      setFilteredMenuItems(sorted);
    } else {
      setFilteredInventoryItems(sorted);
    }
  }, [orderBy, order, tabValue, filteredMenuItems, filteredInventoryItems]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, refreshKey]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderStatsGrid = () => {
    const statItems = [
      { 
        title: 'Total Menu Items', 
        value: stats.menu_item_count || 0, 
        icon: <MenuIcon fontSize="small" />, 
        color: locationColor 
      },
      { 
        title: 'With Inventory', 
        value: stats.menu_items_with_inventory || 0, 
        icon: <StoreIcon fontSize="small" />, 
        color: '#4caf50' 
      },
      { 
        title: 'Total Inventory Items', 
        value: stats.total_items || 0, 
        icon: <InventoryIcon fontSize="small" />, 
        color: '#2196f3' 
      },
      { 
        title: 'Low Stock', 
        value: stats.low_stock_count || 0, 
        icon: <WarningIcon fontSize="small" />, 
        color: '#ff9800' 
      },
      { 
        title: 'Out of Stock', 
        value: stats.out_of_stock_count || 0, 
        icon: <CancelIcon fontSize="small" />, 
        color: '#f44336' 
      },
      { 
        title: 'Total Value', 
        value: `₦${(stats.total_value || 0).toFixed(2)}`, 
        icon: <MoneyIcon fontSize="small" />, 
        color: '#9c27b0' 
      },
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={item.title}>
            <StatCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCharts = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trend Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Stock Movement Trend (Last 14 Days)
            </Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer>
                <ComposedChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="purchases" 
                    fill={alpha('#4caf50', 0.3)} 
                    stroke="#4caf50" 
                    name="Purchases"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    fill={alpha('#f44336', 0.3)} 
                    stroke="#f44336" 
                    name="Sales"
                  />
                  <Bar dataKey="adjustments" fill="#ff9800" name="Adjustments" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Category Distribution Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Category Distribution
            </Typography>
            <Box sx={{ height: 270, width: '100%' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Inventory Dashboard...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />
            
            <div className="epos-content">
              {/* Hero Card */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {locationConfig.icon} {locationName} Inventory Management
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Manage menu items and track inventory in one place
                    </div>
                  </div>
                </div>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    setSnackbar({
                      open: true,
                      message: '📊 Export functionality coming soon',
                      severity: 'info',
                    });
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Export Report
                </Button>
              </div>

              {/* Stats Grid */}
              {renderStatsGrid()}

              {/* Charts Section */}
              {renderCharts()}

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={tabValue} 
                  onChange={(_, v) => setTabValue(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="📋 Menu Items with Inventory" />
                  <Tab label="📦 Inventory Items" />
                  <Tab label="📈 Stock Movements" />
                  <Tab label="⚠️ Alerts" />
                </Tabs>
              </Box>

              {/* ============================================ */}
              {/* TAB 0: MENU ITEMS WITH INVENTORY */}
              {/* ============================================ */}
              {tabValue === 0 && (
                <Box>
                  {/* Search and Filters */}
                  <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="Search menu items by name, category..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{ flex: 1, minWidth: 200 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Clear All Filters">
                            <IconButton onClick={handleClearAllFilters} size="small">
                              <ClearIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Refresh">
                            <IconButton onClick={() => setRefreshKey(prev => prev + 1)} size="small">
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FilterIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Filter by:
                          </Typography>
                        </Box>
                        
                        <ToggleButtonGroup
                          value={mainCategoryFilter}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) setMainCategoryFilter(newValue);
                          }}
                          size="small"
                          aria-label="main category filter"
                        >
                          <ToggleButton value="all" aria-label="all">All</ToggleButton>
                          <ToggleButton value="Food" aria-label="food">
                            <FoodIcon sx={{ fontSize: 16, mr: 0.5 }} /> Food
                          </ToggleButton>
                          <ToggleButton value="Drinks" aria-label="drinks">
                            <DrinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> Drinks
                          </ToggleButton>
                        </ToggleButtonGroup>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={menuStatusFilter}
                            onChange={(e) => setMenuStatusFilter(e.target.value)}
                            label="Status"
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Stock Status</InputLabel>
                          <Select
                            value={stockStatusFilter}
                            onChange={(e) => setStockStatusFilter(e.target.value)}
                            label="Stock Status"
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="has_inventory">Has Inventory</MenuItem>
                            <MenuItem value="no_inventory">No Inventory</MenuItem>
                            <MenuItem value="in_stock">In Stock</MenuItem>
                            <MenuItem value="low_stock">Low Stock</MenuItem>
                            <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Menu Items Table */}
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {loading && <LinearProgress />}
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleSort('name')}
                              >
                                Item Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'category'}
                                direction={orderBy === 'category' ? order : 'asc'}
                                onClick={() => handleSort('category')}
                              >
                                Category
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Main Category</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'amount'}
                                direction={orderBy === 'amount' ? order : 'asc'}
                                onClick={() => handleSort('amount')}
                              >
                                Price (₦)
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Inventory</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredMenuItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                <MenuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                  No Menu Items Found
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {menuItems.length > 0 ? 'Try adjusting your search or filters' : 'No menu items available for this location'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredMenuItems
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((item, index) => {
                                const inventoryItem = inventoryMap[item.id];
                                return (
                                  <MenuItemWithInventoryRow
                                    key={item.id}
                                    item={item}
                                    inventoryItem={inventoryItem}
                                    index={page * rowsPerPage + index}
                                    onEdit={() => {/* Edit menu item - navigate to menu items page */}}
                                    onDelete={handleOpenDeleteDialog}
                                    onToggle={handleOpenToggleDialog}
                                    onAddStock={handleOpenStockDialog}
                                    onViewStock={handleViewStock}
                                    onViewDetails={handleViewItem}
                                  />
                                );
                              })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      component="div"
                      count={filteredMenuItems.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      sx={{
                        '& .MuiTablePagination-displayedRows': {
                          fontWeight: 500,
                        },
                        '& .MuiTablePagination-selectLabel': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Paper>
                </Box>
              )}

              {/* ============================================ */}
              {/* TAB 1: INVENTORY ITEMS */}
              {/* ============================================ */}
              {tabValue === 1 && (
                <Box>
                  {/* Search and Filters */}
                  <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Search inventory..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: 1, minWidth: 200 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setSearchTerm('')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="IN_STOCK">In Stock</MenuItem>
                          <MenuItem value="LOW_STOCK">Low Stock</MenuItem>
                          <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="all">All</MenuItem>
                          {Object.keys(stats.categories || {}).map((cat) => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Clear Filters">
                          <IconButton onClick={handleClearAllFilters} size="small">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh">
                          <IconButton onClick={() => setRefreshKey(prev => prev + 1)} size="small">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Inventory Table */}
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {loading && <LinearProgress />}
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleSort('name')}
                              >
                                Item Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'category'}
                                direction={orderBy === 'category' ? order : 'asc'}
                                onClick={() => handleSort('category')}
                              >
                                Category
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'quantity'}
                                direction={orderBy === 'quantity' ? order : 'asc'}
                                onClick={() => handleSort('quantity')}
                              >
                                Quantity
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel
                                active={orderBy === 'cost_per_unit'}
                                direction={orderBy === 'cost_per_unit' ? order : 'asc'}
                                onClick={() => handleSort('cost_per_unit')}
                              >
                                Cost/Unit
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Linked Menu Item</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredInventoryItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                  No Inventory Items Found
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {inventoryItems.length > 0 ? 'Try adjusting your search or filters' : 'No inventory items available'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredInventoryItems
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((item, index) => (
                                <InventoryItemRow
                                  key={item.id}
                                  item={item}
                                  index={page * rowsPerPage + index}
                                  onEdit={handleOpenDialog}
                                  onDelete={handleOpenDeleteDialog}
                                  onAddStock={handleOpenStockDialog}
                                  onViewMovements={handleOpenMovementsDialog}
                                  onViewDetails={handleViewItem}
                                />
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      component="div"
                      count={filteredInventoryItems.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      sx={{
                        '& .MuiTablePagination-displayedRows': {
                          fontWeight: 500,
                        },
                        '& .MuiTablePagination-selectLabel': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Paper>
                </Box>
              )}

              {/* ============================================ */}
              {/* TAB 2: STOCK MOVEMENTS */}
              {/* ============================================ */}
              {tabValue === 2 && (
                <Box>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      All Stock Movements
                    </Typography>
                    {movements.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No stock movements recorded
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {movements.map((movement) => {
                              const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
                              const typeColor = MOVEMENT_TYPE_COLORS[movement.movement_type] || '#9e9e9e';
                              const typeLabel = MOVEMENT_TYPE_LABELS[movement.movement_type] || movement.movement_type;
                              
                              return (
                                <TableRow key={movement.id}>
                                  <TableCell>{new Date(movement.created_at).toLocaleString()}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={typeLabel}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(typeColor, 0.1),
                                        color: typeColor,
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{movement.inventory_item_name || '-'}</TableCell>
                                  <TableCell>{movement.quantity} {movement.unit}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
                                    {isIn ? '+' : '-'}{movement.quantity}
                                  </TableCell>
                                  <TableCell>{movement.reason || '-'}</TableCell>
                                  <TableCell>{movement.performed_by_name || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Box>
              )}

              {/* ============================================ */}
              {/* TAB 3: ALERTS */}
              {/* ============================================ */}
              {tabValue === 3 && (
                <Box>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Inventory Alerts
                    </Typography>
                    {alerts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50' }} />
                        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                          No Active Alerts
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          All inventory items are at healthy stock levels
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {alerts.map((alert) => (
                          <ListItem
                            key={alert.id}
                            divider
                            sx={{
                              backgroundColor: alpha(
                                alert.alert_type === 'LOW_STOCK' ? '#ff9800' : '#f44336',
                                0.05
                              ),
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <ListItemIcon>
                              {alert.alert_type === 'LOW_STOCK' ? (
                                <WarningIcon color="warning" />
                              ) : (
                                <CancelIcon color="error" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={alert.message}
                              secondary={
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Current: {alert.current_quantity}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Threshold: {alert.threshold}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Created: {new Date(alert.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={async () => {
                                try {
                                  await apiRequest(`/inventory/alerts/${alert.id}/resolve`, {
                                    method: 'PATCH',
                                  });
                                  await fetchAllData();
                                  setSnackbar({
                                    open: true,
                                    message: 'Alert resolved successfully',
                                    severity: 'success',
                                  });
                                } catch (err) {
                                  setSnackbar({
                                    open: true,
                                    message: `Failed to resolve alert: ${err.message}`,
                                    severity: 'error',
                                  });
                                }
                              }}
                            >
                              Resolve
                            </Button>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Box>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ============================================ */}
      {/* TOGGLE DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={toggleDialogOpen}
        onClose={handleCloseToggleDialog}
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, p: 1, maxWidth: '500px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {itemToToggle?.is_active ? (
            <CancelIcon color="warning" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          <Typography variant="h6" fontWeight={700}>
            {itemToToggle?.is_active ? 'Deactivate' : 'Activate'} Menu Item
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <InfoIcon color="info" sx={{ mt: 0.5 }} />
            <Typography variant="body1" fontWeight={400}>
              Are you sure you want to <strong>{itemToToggle?.is_active ? 'deactivate' : 'activate'}</strong> menu item <strong>"{itemToToggle?.name}"</strong>?
            </Typography>
          </Box>
          
          <Alert severity={itemToToggle?.is_active ? 'warning' : 'info'} sx={{ mt: 2 }}>
            <Typography fontWeight={400}>
              {itemToToggle?.is_active 
                ? 'Deactivating will hide this item from the menu. Customers will not be able to order it.'
                : 'Activating will make this item visible in the menu again. Customers will be able to order it.'}
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseToggleDialog} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleToggleMenuItemStatus(itemToToggle?.id, itemToToggle?.is_active)}
            variant="contained"
            color={itemToToggle?.is_active ? 'warning' : 'success'}
            startIcon={itemToToggle?.is_active ? <CancelIcon /> : <CheckCircleIcon />}
            disabled={toggling}
            sx={{ fontWeight: 600 }}
          >
            {toggling ? <CircularProgress size={24} /> : (itemToToggle?.is_active ? 'Deactivate' : 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* DELETE CONFIRMATION DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" fontWeight={700} color="error">
            Permanent Delete
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <WarningIcon color="error" sx={{ mt: 0.5, fontSize: 32 }} />
            <Box>
              <Typography variant="body1" fontWeight={400} gutterBottom>
                You are about to <strong style={{ color: '#d32f2f' }}>permanently delete</strong> <strong>"{itemToDelete?.name}"</strong>.
              </Typography>
              <Typography variant="body2" color="textSecondary" fontWeight={400}>
                This action <strong style={{ color: '#d32f2f' }}>cannot be undone</strong>.
              </Typography>
            </Box>
          </Box>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={400}>
              <strong>Warning:</strong> This will remove the item from all associated records.
            </Typography>
          </Alert>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500} gutterBottom>
              To confirm, type <strong>"{itemToDelete?.name}"</strong>:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={`Type "${itemToDelete?.name}" to confirm`}
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              error={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name}
              helperText={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name ? 'Name does not match' : ''}
              autoFocus
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (itemToDelete?.id) {
                handleDeleteMenuItem(itemToDelete.id);
              }
            }}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleting || deleteConfirmation !== itemToDelete?.name}
            sx={{ fontWeight: 600 }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* ADD STOCK DIALOG WITH STEPPER */}
      {/* ============================================ */}
      <Dialog
        open={stockDialogOpen}
        onClose={handleCloseStockDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <AddIcon sx={{ mr: 1, color: '#4caf50' }} />
              Add Stock
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stockMenuItem?.name || stockItem?.name} 
              {stockItem?.quantity !== undefined && ` - Current: ${stockItem.quantity} ${stockItem.unit || 'unit'}`}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseStockDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stepper activeStep={stockStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Quantity</StepLabel>
            </Step>
            <Step>
              <StepLabel>Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm</StepLabel>
            </Step>
          </Stepper>

          <Alert severity="info" sx={{ mb: 3 }}>
            {stockItem?.id ? 
              `Adding stock to "${stockItem.name}"` : 
              `This will create an inventory item for "${stockMenuItem?.name}"`}
          </Alert>

          {stockStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quantity to Add"
                  type="number"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  required
                  autoFocus
                  InputProps={{
                    inputProps: { min: 0.01, step: 0.01 },
                    endAdornment: (
                      <InputAdornment position="end">
                        {stockItem?.unit || 'unit'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {stockStep === 1 && (
            <Grid container spacing={3}>
             
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  value={stockReason}
                  onChange={(e) => setStockReason(e.target.value)}
                  placeholder="e.g., New shipment, Re-stock, Transfer"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={stockNotes}
                  onChange={(e) => setStockNotes(e.target.value)}
                  placeholder="Additional notes about this stock addition"
                />
              </Grid>
            </Grid>
          )}

          {stockStep === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Confirm Stock Addition
              </Typography>
              <Box sx={{ bgcolor: alpha('#4caf50', 0.04), p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {stockMenuItem?.name || stockItem?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quantity</Typography>
                    <Typography variant="body2" fontWeight={700} color="#4caf50">
                      +{stockQuantity} {stockItem?.unit || 'unit'}
                    </Typography>
                  </Grid>
                 
                  {stockReason && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Reason</Typography>
                      <Typography variant="body2">{stockReason}</Typography>
                    </Grid>
                  )}
                  {stockNotes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{stockNotes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseStockDialog} color="inherit">
            Cancel
          </Button>
          {stockStep > 0 && (
            <Button onClick={() => setStockStep(prev => prev - 1)}>
              Back
            </Button>
          )}
          {stockStep < 2 ? (
            <Button
              variant="contained"
              onClick={() => setStockStep(prev => prev + 1)}
              disabled={(stockStep === 0 && !stockQuantity)}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddStock}
              disabled={stocking || !stockQuantity}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              {stocking ? <CircularProgress size={24} /> : 'Confirm Add Stock'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* VIEW STOCK DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={viewStockDialogOpen}
        onClose={handleCloseViewStockDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StoreIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              Stock Information: {viewStockItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewStockItem?.category} • ₦{viewStockItem?.amount?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewStockDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingStockData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewStockData ? (
            <>
              {/* Stock Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#4caf50', 0.04), borderLeft: '4px solid #4caf50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {viewStockData.quantity} {viewStockData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#ff9800', 0.04), borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {viewStockData.reorder_point} {viewStockData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#2196f3', 0.04), borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Chip
                        label={viewStockData.status?.replace('_', ' ') || 'Unknown'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: alpha(
                            viewStockData.status === 'IN_STOCK' ? '#4caf50' :
                            viewStockData.status === 'LOW_STOCK' ? '#ff9800' :
                            viewStockData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                            0.1
                          ),
                          color: viewStockData.status === 'IN_STOCK' ? '#4caf50' :
                                 viewStockData.status === 'LOW_STOCK' ? '#ff9800' :
                                 viewStockData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#9c27b0', 0.04), borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Value</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        ₦{((viewStockData.quantity || 0) * (viewStockData.cost_per_unit || 0)).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Stock Movements History */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                Stock Movement History
              </Typography>
              
              {viewStockMovements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No stock movements recorded for this item
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Previous</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewStockMovements.slice(0, 50).map((movement) => {
                        const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
                        return (
                          <TableRow key={movement.id}>
                            <TableCell>
                              {new Date(movement.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={movement.movement_type}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    isIn ? '#4caf50' : '#f44336',
                                    0.1
                                  ),
                                  color: isIn ? '#4caf50' : '#f44336',
                                  fontWeight: 600,
                                }}
                                icon={isIn ? <InIcon fontSize="small" /> : <OutIcon fontSize="small" />}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
                              {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
                            </TableCell>
                            <TableCell>{movement.previous_quantity}</TableCell>
                            <TableCell>{movement.new_quantity}</TableCell>
                            <TableCell>{movement.reason || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No Inventory Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No inventory item has been created for this menu item yet.
                Use the "Add Stock" button to create one.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseViewStockDialog} color="inherit">
            Close
          </Button>
          {viewStockData && (
            <Button
              variant="outlined"
              startIcon={<StockIcon />}
              onClick={() => {
                handleCloseViewStockDialog();
                handleOpenStockDialog(viewStockItem, viewStockData);
              }}
            >
              Add More Stock
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* VIEW DETAILS DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={700}>Item Details</Typography>
          <IconButton onClick={handleCloseViewDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {viewItem && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{viewItem.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1">{viewItem.category || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Unit</Typography>
                  <Typography variant="body1">{viewItem.unit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Quantity</Typography>
                  <Typography variant="body1" fontWeight={600}>{viewItem.quantity}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip
                    label={STATUS_LABELS[viewItem.status] || viewItem.status}
                    size="small"
                    sx={{
                      backgroundColor: alpha(STATUS_COLORS[viewItem.status] || '#9e9e9e', 0.1),
                      color: STATUS_COLORS[viewItem.status] || '#9e9e9e',
                      fontWeight: 600,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Cost Per Unit</Typography>
                  <Typography variant="body1">₦{viewItem.cost_per_unit?.toFixed(2) || '0.00'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Value</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    ₦{((viewItem.quantity || 0) * (viewItem.cost_per_unit || 0)).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Min Stock Level</Typography>
                  <Typography variant="body1">{viewItem.minimum_stock_level || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                  <Typography variant="body1">{viewItem.reorder_point || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Supplier</Typography>
                  <Typography variant="body1">{viewItem.supplier || '-'}</Typography>
                </Grid>
                {viewItem.menu_item_name && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Linked Menu Item</Typography>
                    <Chip label={viewItem.menu_item_name} size="small" icon={<LinkIcon sx={{ fontSize: 14 }} />} />
                  </Grid>
                )}
                {viewItem.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{viewItem.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* SNACKBAR */}
      {/* ============================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ============================================ */}
      {/* FLOATING ACTION BUTTON */}
      {/* ============================================ */}
      <Fab
        color="primary"
        aria-label="add inventory item"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
          backgroundColor: locationColor,
          '&:hover': {
            backgroundColor: locationColor,
            opacity: 0.8,
          },
        }}
        onClick={() => handleOpenDialog('create')}
      >
        <AddIcon />
      </Fab>
    </Suspense>
  );
};

export default InventoryDashboard;